import { calculateBearing } from "../lab-v14/core/calculateBearing";
import { calculateDistance } from "../lab-v14/core/calculateDistance";
import { calculateRelativeAngle } from "../lab-v14/core/calculateRelativeAngle";
import type { GeoFeatureType } from "../lab-v14/types/GeoFeature";
import type { GeoArAccuracyWarning, GeoArStatus } from "../lab-v14/types/GeoArState";
import type { GeoArResult } from "../lab-v14/types/GeoArResult";
import {
  getMapLabelIndexSummary,
  loadMapLabelTuples,
  scheduleMapLabelIndexForPack,
} from "../../../../lib/android-map-label-index";
import { mergeMapLabelTuples } from "../../../../lib/map-label-index-core";
import { mapPackForPoint } from "../../../../../shared/ecuador-map-packs";
import { registerElevationTargetV4 } from "./elevationResolverV4";

export type PointIdentifyGazetteerTuple = [
  id: string,
  name: string,
  aliases: string[],
  latitude: number,
  longitude: number,
  kind: string,
  rank: number,
  packIds: string[],
  elevationM?: number | null,
  elevationSource?: string | null,
  elevationStatus?: string | null,
];

type GazetteerPayload = {
  schemaVersion?: number;
  source?: string;
  license?: string;
  attribution?: string;
  places?: PointIdentifyGazetteerTuple[];
};

export type PointIdentifyCatalogQuery = {
  latitude: number;
  longitude: number;
  accuracy: number;
  heading: number;
  headingAccuracy: number | null;
  horizontalFov: number;
  offline: boolean;
  allowedPackIds?: readonly string[];
  maxResults?: number;
};

export type PointIdentifyCatalogScan = {
  results: GeoArResult[];
  source: string;
  license: string | null;
  scanned: number;
  nearby: number;
  mapCatalogScanned: number;
  staticCatalogScanned: number;
  mapCatalogReady: boolean;
  mapCatalogBuilding: boolean;
};

const LOCAL_GAZETTEER_URL = "/search/ecuador-gazetteer.json";
const DEFAULT_PACK_IDS = ["sierra-sur", "azuay", "canar", "ecuador"] as const;
let gazetteerPromise: Promise<GazetteerPayload> | null = null;

function localAssetUrl() {
  if (typeof document !== "undefined" && document.baseURI) {
    return new URL(LOCAL_GAZETTEER_URL.replace(/^\//, ""), document.baseURI).toString();
  }
  return LOCAL_GAZETTEER_URL;
}

async function loadGazetteerPayload(): Promise<GazetteerPayload> {
  if (!gazetteerPromise) {
    gazetteerPromise = fetch(localAssetUrl(), {
      cache: "force-cache",
      headers: { Accept: "application/json" },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("POINT_IDENTIFY_GAZETTEER_UNAVAILABLE");
        const payload = await response.json() as GazetteerPayload;
        if (!Array.isArray(payload.places)) throw new Error("POINT_IDENTIFY_GAZETTEER_INVALID");
        return payload;
      })
      .catch((error) => {
        gazetteerPromise = null;
        throw error;
      });
  }
  return gazetteerPromise;
}

export function warmPointIdentifyCatalog() {
  return loadGazetteerPayload().then(() => undefined).catch(() => undefined);
}

function normalizeKind(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es")
    .trim();
}

export function geoFeatureTypeFromGazetteerKind(kind: string): GeoFeatureType | null {
  const normalized = normalizeKind(kind);
  if (normalized.includes("volcan")) return "VOLCANO";
  if (normalized.includes("cerro") || normalized.includes("montana")) return "SUMMIT";
  if (normalized.includes("laguna")) return "LAGOON";
  if (normalized.includes("lago")) return "LAKE";
  if (normalized.includes("rio") || normalized.includes("quebrada") || normalized === "agua") return "RIVER";
  if (
    normalized.includes("localidad") ||
    normalized.includes("provincia") ||
    normalized.includes("canton") ||
    normalized.includes("parroquia") ||
    normalized.includes("administrativo")
  ) return "PLACE";
  if (
    normalized.includes("via") ||
    normalized.includes("ruta") ||
    normalized.includes("parque") ||
    normalized.includes("area") ||
    normalized.includes("sitio") ||
    normalized.includes("relieve") ||
    normalized.includes("terreno")
  ) return "GEOGRAPHIC_POINT";
  return null;
}

function maxDistanceForType(type: GeoFeatureType) {
  switch (type) {
    case "MOUNTAIN":
    case "VOLCANO":
    case "SUMMIT":
      return 50_000;
    case "HILL":
      return 25_000;
    case "LAGOON":
    case "LAKE":
      return 15_000;
    case "RIVER":
      return 4_000;
    case "PLACE":
      return 5_000;
    case "VIEWPOINT":
    case "REFUGE":
    case "MOUNTAIN_PASS":
    case "GEOGRAPHIC_POINT":
      return 5_000;
    default:
      return 5_000;
  }
}

function accuracyWarnings(accuracy: number, headingAccuracy: number | null): GeoArAccuracyWarning[] {
  const warnings: GeoArAccuracyWarning[] = [];
  if (accuracy > 25) warnings.push("LOW_GPS_ACCURACY");
  if (headingAccuracy === null || headingAccuracy > 20) warnings.push("LOW_HEADING_ACCURACY");
  return warnings;
}

function accuracyScore(accuracy: number, headingAccuracy: number | null) {
  const gps = accuracy <= 15 ? 1 : accuracy <= 25 ? 0.9 : accuracy <= 50 ? 0.72 : 0.55;
  const heading = headingAccuracy === null
    ? 0.72
    : headingAccuracy <= 10
      ? 1
      : headingAccuracy <= 20
        ? 0.9
        : headingAccuracy <= 40
          ? 0.7
          : 0.52;
  return gps * 0.45 + heading * 0.55;
}

function candidateScore(input: {
  relativeAngle: number;
  aimDegrees: number;
  distance: number;
  maxDistance: number;
  rank: number;
  accuracy: number;
  headingAccuracy: number | null;
}) {
  const alignment = Math.max(0, 1 - Math.abs(input.relativeAngle) / input.aimDegrees);
  const distance = Math.max(0, 1 - Math.min(1, input.distance / input.maxDistance));
  const rank = Math.max(0, Math.min(1, input.rank / 1000));
  const accuracy = accuracyScore(input.accuracy, input.headingAccuracy);
  // Point & Identify V1.2 is deliberately near-first. Camera alignment still
  // matters most, but a distant label can no longer win merely because it sits
  // on the same bearing. This is especially important for rivers, roads and
  // settlements where a nearby feature should dominate a remote label.
  return Math.min(0.9, alignment * 0.52 + distance * 0.28 + accuracy * 0.15 + rank * 0.05);
}

function packAllowed(packIds: readonly string[], allowed: ReadonlySet<string>) {
  return packIds.some((packId) => allowed.has(packId));
}

function sourceForTuple(id: string, payload: GazetteerPayload) {
  if (id.startsWith("map-")) return "THERA Map Catalog · PMTiles";
  return `THERA Gazetteer · ${payload.attribution || payload.source || "GeoNames"}`;
}

function mountainElevationMetadata(
  featureType: GeoFeatureType,
  elevationM: unknown,
  elevationSource: unknown,
  elevationStatus: unknown,
) {
  if (!["MOUNTAIN", "VOLCANO", "SUMMIT", "HILL"].includes(featureType)) {
    return { altitude: null, source: null, status: null };
  }
  if (elevationM === null || elevationM === undefined || elevationM === "") {
    return { altitude: null, source: null, status: null };
  }
  const altitude = Number(elevationM);
  if (!Number.isFinite(altitude) || altitude < -500 || altitude > 9_000) {
    return { altitude: null, source: null, status: null };
  }
  return {
    altitude,
    source: typeof elevationSource === "string" && elevationSource.trim() ? elevationSource : null,
    status: typeof elevationStatus === "string" && elevationStatus.trim() ? elevationStatus : null,
  };
}

function scanPointIdentifyTuples(
  tuples: PointIdentifyGazetteerTuple[],
  query: PointIdentifyCatalogQuery,
  payload: GazetteerPayload,
) {
  const allowed = new Set(query.allowedPackIds ?? DEFAULT_PACK_IDS);
  const horizontalFov = Math.max(30, Math.min(120, query.horizontalFov));
  const aimDegrees = Math.max(4, Math.min(10, horizontalFov * 0.14));
  const broadAimDegrees = Math.min(horizontalFov / 2, aimDegrees * 1.35);
  const globalMaxDistance = 50_000;
  const latDelta = globalMaxDistance / 110_900;
  const lonScale = Math.max(0.25, Math.cos((query.latitude * Math.PI) / 180));
  const lonDelta = globalMaxDistance / (111_320 * lonScale);
  const warnings = accuracyWarnings(query.accuracy, query.headingAccuracy);
  const maxResults = Math.max(1, Math.min(30, query.maxResults ?? 12));
  let nearby = 0;
  const candidates: Array<{ result: GeoArResult; score: number }> = [];

  for (const tuple of tuples) {
    if (!Array.isArray(tuple) || tuple.length < 8) continue;
    const [id, name, , latitude, longitude, kind, rank, packIds, elevationM, elevationSource, elevationStatus] = tuple;
    if (!id || !name || !Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;
    const normalizedPackIds = Array.isArray(packIds) ? packIds : [];
    if (!packAllowed(normalizedPackIds, allowed)) continue;
    const featureType = geoFeatureTypeFromGazetteerKind(String(kind ?? ""));
    if (!featureType) continue;
    registerElevationTargetV4({
      featureUuid: String(id),
      latitude,
      longitude,
      packIds: normalizedPackIds,
    });
    const featureElevation = mountainElevationMetadata(featureType, elevationM, elevationSource, elevationStatus);
    if (Math.abs(latitude - query.latitude) > latDelta || Math.abs(longitude - query.longitude) > lonDelta) continue;
    nearby += 1;

    const maxDistance = maxDistanceForType(featureType);
    const distance = calculateDistance(query.latitude, query.longitude, latitude, longitude);
    if (distance > maxDistance) continue;
    const bearing = calculateBearing(query.latitude, query.longitude, latitude, longitude);
    const relativeAngle = calculateRelativeAngle(bearing, query.heading);
    if (Math.abs(relativeAngle) > broadAimDegrees) continue;

    let score = candidateScore({
      relativeAngle,
      aimDegrees,
      distance,
      maxDistance,
      rank: Number(rank) || 0,
      accuracy: query.accuracy,
      headingAccuracy: query.headingAccuracy,
    });
    // Labels extracted from the user's downloaded THERA map are more useful for
    // Point & Identify than the tiny bootstrap catalog. Give nearby map-derived
    // hydrology a small preference without overriding camera alignment.
    if (String(id).startsWith("map-")) score = Math.min(0.92, score + (featureType === "RIVER" ? 0.05 : 0.025));

    const visibilityState = Math.abs(relativeAngle) <= horizontalFov / 2 ? "VISIBLE" : "OUTSIDE_VIEW";
    const status: GeoArStatus[] = [visibilityState, ...warnings];
    if (query.offline) status.push("OFFLINE_AVAILABLE");

    candidates.push({
      score,
      result: {
        feature_uuid: String(id),
        name: String(name),
        feature_type: featureType,
        distance,
        bearing,
        relative_angle: relativeAngle,
        elevation_angle: null,
        relative_vertical_angle: null,
        visibility_state: visibilityState,
        confidence: score,
        candidate_rank: null,
        alignment_warning: false,
        accuracy_warning: [...warnings],
        data_source: sourceForTuple(String(id), payload),
        feature_altitude_m: featureElevation.altitude,
        feature_altitude_source: featureElevation.source,
        feature_altitude_status: featureElevation.status,
        offline: query.offline,
        status,
      },
    });
  }

  candidates.sort((left, right) => right.score - left.score
    || Math.abs(left.result.relative_angle ?? Infinity) - Math.abs(right.result.relative_angle ?? Infinity)
    || (left.result.distance ?? Infinity) - (right.result.distance ?? Infinity));

  return {
    results: candidates.slice(0, maxResults).map(({ result }, index) => ({ ...result, candidate_rank: index + 1 })),
    nearby,
  };
}

async function loadRegionalMapCatalog(packIds: readonly string[]) {
  const regionalPackIds = [...new Set(packIds.filter((packId) => packId && packId !== "ecuador"))];
  if (!regionalPackIds.length || typeof indexedDB === "undefined") {
    return { tuples: [] as PointIdentifyGazetteerTuple[], ready: false, building: false };
  }

  // Reuse the exact offline label index already used by THERA search. This does
  // not change or rewrite the maps; it only reads the names extracted from them.
  await Promise.all(regionalPackIds.map((packId, index) =>
    scheduleMapLabelIndexForPack(packId, index === 0).catch(() => null),
  ));

  const packSet = new Set(regionalPackIds);
  const [tuples, summary] = await Promise.all([
    loadMapLabelTuples(packSet).catch(() => []),
    getMapLabelIndexSummary(packSet).catch(() => ({ building: false, readyCount: 0 })),
  ]);
  return {
    tuples: tuples as PointIdentifyGazetteerTuple[],
    ready: Number(summary.readyCount ?? 0) > 0,
    building: Boolean(summary.building),
  };
}

export async function scanPointIdentifyCatalog(query: PointIdentifyCatalogQuery): Promise<PointIdentifyCatalogScan> {
  const payload = await loadGazetteerPayload();
  const staticTuples = payload.places ?? [];
  const allowedPackIds = query.allowedPackIds ?? DEFAULT_PACK_IDS;
  const preferredPack = mapPackForPoint(query.longitude, query.latitude);
  const orderedPackIds = preferredPack?.id && allowedPackIds.includes(preferredPack.id)
    ? [preferredPack.id, ...allowedPackIds.filter((packId) => packId !== preferredPack.id)]
    : [...allowedPackIds];
  const mapCatalog = await loadRegionalMapCatalog(orderedPackIds);
  const tuples = mergeMapLabelTuples(staticTuples, mapCatalog.tuples) as PointIdentifyGazetteerTuple[];
  const scan = scanPointIdentifyTuples(tuples, query, payload);
  const mapCatalogScanned = mapCatalog.tuples.length;
  const source = mapCatalogScanned
    ? `THERA Map Catalog (${mapCatalogScanned}) + ${payload.attribution || payload.source || "GeoNames"}`
    : (payload.attribution || payload.source || "GeoNames");

  return {
    results: scan.results,
    source,
    license: payload.license ?? null,
    scanned: tuples.length,
    nearby: scan.nearby,
    mapCatalogScanned,
    staticCatalogScanned: staticTuples.length,
    mapCatalogReady: mapCatalog.ready,
    mapCatalogBuilding: mapCatalog.building,
  };
}
